import type { Mode } from '@/types/entity-mode';
import type { EventDto, EventSlotDto } from '@/types/dtos/events/response/event.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { EVENT_SLOT_TYPE } from '@/types/dtos/events/event-slot-content.enum';
import { getObjectId } from '@/helpers/object.id.helper';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import type { MediaPlaylistDto } from '@/types/dtos/media/media-playlist.dto';
import { pickFresherMedia } from '@/helpers/flow-media-playlist.helper';

export const isDebugFlowEnabled = () =>
  import.meta.env.DEV && Boolean((window as { __DEBUG_FLOW__?: boolean }).__DEBUG_FLOW__);

export const debugFlow = (...args: unknown[]) => {
  if (isDebugFlowEnabled()) {
    console.log(...args);
  }
};

const sessionPlaylistRefCount = (session?: SessionDto) => session?.mediaPlaylists?.session?.refs?.length ?? 0;
const sessionContributorPlaylistRefCount = (session?: SessionDto) =>
  session?.contributorPlaylists?.session?.refs?.length ?? 0;

const contributorPlaylistCanReorder = (playlist?: { permissions?: string[] }) =>
  Boolean(playlist?.permissions?.includes('reorder') || playlist?.permissions?.includes('manage'));

const mergeMediaListsByFreshness = (primary?: MediaDto[], secondary?: MediaDto[]): MediaDto[] | undefined => {
  if (!primary?.length) return secondary;
  if (!secondary?.length) return primary;

  const byId = new Map<string, MediaDto>();
  for (const media of primary) {
    byId.set(media.id, media);
  }
  for (const media of secondary) {
    byId.set(media.id, pickFresherMedia(byId.get(media.id), media) ?? media);
  }

  const seen = new Set<string>();
  const ordered: MediaDto[] = [];
  for (const media of primary) {
    const fresher = byId.get(media.id);
    if (fresher) {
      ordered.push(fresher);
      seen.add(media.id);
    }
  }
  for (const media of secondary) {
    if (seen.has(media.id)) continue;
    const fresher = byId.get(media.id);
    if (fresher) ordered.push(fresher);
  }
  return ordered;
};

const mergePlaylistRefsWithFresherMedia = (
  primary?: MediaPlaylistDto,
  secondary?: MediaPlaylistDto,
  mediaFallback?: MediaDto[],
): MediaPlaylistDto | undefined => {
  if (!primary?.refs?.length) return secondary ?? primary;
  if (!secondary?.refs?.length && !mediaFallback?.length) return primary;

  const secondaryByMediaId = new Map((secondary?.refs ?? []).map((ref) => [ref.mediaId, ref] as const));
  const mediaById = new Map((mediaFallback ?? []).map((media) => [media.id, media]));

  let changed = false;
  const refs = primary.refs.map((ref) => {
    const fromSecondary = secondaryByMediaId.get(ref.mediaId)?.media;
    const fromFallback = mediaById.get(ref.mediaId);
    const fresher = pickFresherMedia(pickFresherMedia(ref.media, fromSecondary), fromFallback);
    if (!fresher || fresher === ref.media) return ref;
    changed = true;
    return { ...ref, media: fresher };
  });

  if (!changed) return primary;
  return { ...primary, refs };
};

/**
 * Prefer playlist metadata from whichever source has richer session playlist data.
 * Also backfill media-settings fields: event-store / slot copies often omit
 * mediaSettingsCapabilities while GET session includes them — without this, empty
 * playlists keep the store copy and create/reorder permission controls stay locked.
 *
 * Always reconcile embedded playlist media with the fresher asset list — a higher
 * session.version (e.g. after approve) must not keep an older assets[] that omits
 * an in-flight upload while the other source already has it.
 */
const mergeSessionPlaylistFields = (primary: SessionDto, secondary?: SessionDto): SessionDto => {
  if (!secondary) return primary;

  const primaryRefs = sessionPlaylistRefCount(primary);
  const secondaryRefs = sessionPlaylistRefCount(secondary);
  const secondaryPlaylist = secondary.mediaPlaylists?.session;
  const primaryPlaylist = primary.mediaPlaylists?.session;

  let merged = primary;

  if (secondaryPlaylist?.id) {
    if (!primaryPlaylist?.id || secondaryRefs > primaryRefs) {
      merged = {
        ...merged,
        mediaPlaylists: secondary.mediaPlaylists,
        media: (secondary.media?.length ?? 0) > (primary.media?.length ?? 0) ? secondary.media : merged.media,
      };
    } else if (
      primaryPlaylist.id === secondaryPlaylist.id &&
      secondaryPlaylist.permissions?.includes('reorder') &&
      !primaryPlaylist.permissions?.includes('reorder')
    ) {
      merged = {
        ...merged,
        mediaPlaylists: {
          session: { ...primaryPlaylist, permissions: secondaryPlaylist.permissions },
        },
      };
    }
  }

  const mergedMedia = mergeMediaListsByFreshness(merged.media, secondary.media);
  const mergedPlaylist = mergePlaylistRefsWithFresherMedia(
    merged.mediaPlaylists?.session,
    secondary.mediaPlaylists?.session,
    mergedMedia,
  );
  if (mergedMedia !== merged.media || mergedPlaylist !== merged.mediaPlaylists?.session) {
    merged = {
      ...merged,
      media: mergedMedia ?? merged.media,
      ...(mergedPlaylist ? { mediaPlaylists: { ...merged.mediaPlaylists, session: mergedPlaylist } } : {}),
    };
  }

  const primaryContributorRefs = sessionContributorPlaylistRefCount(merged);
  const secondaryContributorRefs = sessionContributorPlaylistRefCount(secondary);
  const secondaryContributorPlaylist = secondary.contributorPlaylists?.session;
  const primaryContributorPlaylist = merged.contributorPlaylists?.session;

  if (secondaryContributorPlaylist?.id) {
    if (!primaryContributorPlaylist?.id || secondaryContributorRefs > primaryContributorRefs) {
      merged = {
        ...merged,
        contributorPlaylists: secondary.contributorPlaylists,
      };
    } else if (
      primaryContributorPlaylist.id === secondaryContributorPlaylist.id &&
      contributorPlaylistCanReorder(secondaryContributorPlaylist) &&
      !contributorPlaylistCanReorder(primaryContributorPlaylist)
    ) {
      merged = {
        ...merged,
        contributorPlaylists: {
          session: { ...primaryContributorPlaylist, permissions: secondaryContributorPlaylist.permissions },
        },
      };
    }
  }

  return {
    ...merged,
    mediaSettings: merged.mediaSettings ?? secondary.mediaSettings,
    effectiveMediaSettings: merged.effectiveMediaSettings ?? secondary.effectiveMediaSettings,
    mediaSettingsCapabilities: merged.mediaSettingsCapabilities ?? secondary.mediaSettingsCapabilities,
  };
};

const mediaAssetsFingerprint = (mediaList?: SessionDto['media']) =>
  mediaList
    ?.map(
      (media) =>
        `${media.id}:${media.assets?.map((asset) => `${asset.version}:${asset.isApproved ? 1 : 0}`).join(',') ?? ''}`,
    )
    .join('|') ?? '';

const sessionMediaAssetsFingerprint = (session?: SessionDto) => {
  const fromSessionMedia = mediaAssetsFingerprint(session?.media);
  const fromPlaylistRefs =
    session?.mediaPlaylists?.session?.refs
      ?.map((ref) => {
        if (!ref.media?.assets?.length) return `${ref.mediaId}:`;
        return `${ref.mediaId}:${ref.media.assets.map((asset) => `${asset.version}:${asset.isApproved ? 1 : 0}`).join(',')}`;
      })
      .join('|') ?? '';
  return `${fromSessionMedia}|refs:${fromPlaylistRefs}`;
};

export const pickNewerSession = (storeSession?: SessionDto, backendSession?: SessionDto) => {
  if (!backendSession) return storeSession;
  if (!storeSession) return backendSession;

  let picked: SessionDto;
  if (backendSession.version !== storeSession.version) {
    picked = backendSession.version > storeSession.version ? backendSession : storeSession;
  } else if ((backendSession.media?.length ?? 0) !== (storeSession.media?.length ?? 0)) {
    picked = backendSession;
  } else if (sessionPlaylistRefCount(backendSession) !== sessionPlaylistRefCount(storeSession)) {
    picked =
      sessionPlaylistRefCount(backendSession) > sessionPlaylistRefCount(storeSession) ? backendSession : storeSession;
  } else if (sessionMediaAssetsFingerprint(backendSession) !== sessionMediaAssetsFingerprint(storeSession)) {
    picked = backendSession;
  } else {
    picked = storeSession;
  }

  const other = picked === storeSession ? backendSession : storeSession;
  return mergeSessionPlaylistFields(picked, other);
};

type ResolveEventIdsParams = {
  contextTypeParam: string | null;
  contextIdParam: string | null;
  event?: EventDto;
  fetchedEvent?: EventDto;
  eventIdForQuery?: string;
};

const getOriginId = (origin?: EventDto['origin']) => {
  if (!origin) return undefined;
  return typeof origin === 'string' ? origin : getObjectId(origin);
};

export const resolveEventIdForLists = ({
  contextTypeParam,
  contextIdParam,
  event,
  fetchedEvent,
  eventIdForQuery,
}: ResolveEventIdsParams) => {
  if (contextTypeParam === 'event' && contextIdParam) return contextIdParam;
  if (event?.id) return event.id;
  if (fetchedEvent?.id) return fetchedEvent.id;
  return eventIdForQuery;
};

export const resolveEventIdForContributors = ({
  event,
  fetchedEvent,
  eventIdForLists,
}: {
  event?: EventDto;
  fetchedEvent?: EventDto;
  eventIdForLists?: string;
}) => {
  const originFromEvent = getOriginId(event?.origin);
  if (originFromEvent) return originFromEvent;
  const originFromFetched = getOriginId(fetchedEvent?.origin);
  if (originFromFetched) return originFromFetched;
  return eventIdForLists;
};

export const resolveEventSlotForFlow = ({
  event,
  flow,
  eventSlotIdParam,
}: {
  event?: EventDto;
  flow?: FlowDto;
  eventSlotIdParam?: string | null;
}): EventSlotDto | undefined => {
  if (!event?.slots || !flow) return undefined;
  if (eventSlotIdParam) {
    return event.slots.find(
      (slot) =>
        slot.contentType === EVENT_SLOT_TYPE.FLOW && (slot.id === eventSlotIdParam || slot.origin === eventSlotIdParam),
    );
  }

  if (flow.slot?.id) {
    const matchingSlot = event.slots.find(
      (slot) => slot.id === flow.slot?.id && slot.contentType === EVENT_SLOT_TYPE.FLOW,
    );
    if (matchingSlot) return matchingSlot;
  }

  const flowOriginId = flow.origin ? getObjectId(flow.origin) : undefined;
  return event.slots.find(
    (slot) =>
      slot.contentType === EVENT_SLOT_TYPE.FLOW &&
      (slot.content === flow.id || (flowOriginId && slot.content === flowOriginId)),
  );
};

export const selectFlowByMode = ({
  flowMode,
  flowId,
  flowPlan,
  flowOrganize,
  fallbackFlow,
}: {
  flowMode: Record<string, Mode>;
  flowId?: string;
  flowPlan?: FlowDto;
  flowOrganize?: FlowDto;
  fallbackFlow?: FlowDto;
}) => {
  if (!flowId) return flowPlan || flowOrganize || fallbackFlow;

  const flowMatchesId = (flow?: FlowDto) => {
    if (!flow) return false;
    const originId = flow.origin ? getObjectId(flow.origin) : flow.id;
    return originId === flowId;
  };
  const planFlow = flowMatchesId(flowPlan) ? flowPlan : undefined;
  const organizeFlow = flowMatchesId(flowOrganize) ? flowOrganize : undefined;

  const selectedMode = flowMode[flowId];
  if (selectedMode === 'ORGANIZE') {
    return organizeFlow;
  }
  if (selectedMode === 'PLAN') {
    return planFlow;
  }
  if (selectedMode === 'PERFORM') return organizeFlow;
  return planFlow || organizeFlow || fallbackFlow;
};

function findSessionByIdOrOrigin(sessions: SessionDto[] | undefined, contentId: string): SessionDto | undefined {
  return sessions?.find((s) => s.id === contentId || (s.origin != null && getObjectId(s.origin) === contentId));
}

/** Embedded slot session in draft/PLAY layer (PLAY exists at runtime but is not on SessionDto mode typings). */
export function isEmbeddedDraftLayerSession(mode: SessionDto['mode'] | undefined): boolean {
  if (!mode || mode === 'PLAN') return true;
  return (mode as SessionDto['mode'] | 'PLAY') === 'PLAY';
}

/** Stable PLAN session id for API calls from a flow slot (store match, embedded content, plan-slot fallback). */
export function resolveSessionOriginIdFromFlowSlot(
  slot: FlowSlotDto | undefined,
  options: {
    eventSessionsPlan?: SessionDto[];
    eventSessionsOrganize?: SessionDto[];
    flowPlan?: FlowDto | null;
  },
): string | undefined {
  if (!slot || slot.contentType !== 'Session' || !slot.content) return undefined;

  const resolveFromContent = (content: FlowSlotDto['content']): string | undefined => {
    if (!content) return undefined;
    if (typeof content === 'string') {
      const matched =
        findSessionByIdOrOrigin(options.eventSessionsPlan, content) ??
        findSessionByIdOrOrigin(options.eventSessionsOrganize, content);
      if (matched) {
        return matched.origin ? getObjectId(matched.origin) : matched.id;
      }
      return content;
    }
    if (typeof content === 'object') {
      if (content.origin) return getObjectId(content.origin);
      if (content.id) return content.id;
    }
    return undefined;
  };

  const fromSlot = resolveFromContent(slot.content);
  if (fromSlot) {
    const planMatch = options.eventSessionsPlan?.find((s) => {
      const originId = s.origin ? getObjectId(s.origin) : s.id;
      return originId === fromSlot || s.id === fromSlot;
    });
    if (planMatch) {
      return planMatch.origin ? getObjectId(planMatch.origin) : planMatch.id;
    }
  }

  if (slot.origin && options.flowPlan?.slots) {
    const planSlot = options.flowPlan.slots.find((ps) => ps.id === slot.origin);
    if (planSlot?.contentType === 'Session') {
      const fromPlanSlot = resolveFromContent(planSlot.content);
      if (fromPlanSlot) return fromPlanSlot;
    }
  }

  return fromSlot;
}
